import { useState, useEffect, useRef } from "react";
import { tataLogout } from "../services/tataAuth";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import constants from "../common/constants";
import { useApi } from "../hooks";
import {
  apiLogutUser,
  apiChangeOwnPassword,
  apiPath,
  method,
} from "../apiPath";
import keyNames from "../keyName";

import AccountCircle from "@mui/icons-material/AccountCircle";
import LogoutIcon from "@mui/icons-material/Logout";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import BadgeIcon from "@mui/icons-material/Badge";
import CloseIcon from "@mui/icons-material/Close";
import LockResetIcon from "@mui/icons-material/LockReset";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import SpeedIcon from "@mui/icons-material/Speed";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import ChecklistIcon from "@mui/icons-material/Checklist";
import StorageIcon from "@mui/icons-material/Storage";
import EventIcon from "@mui/icons-material/Event";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AssessmentIcon from "@mui/icons-material/Assessment";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import ApprovalIcon from "@mui/icons-material/Approval";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import InboxIcon from "@mui/icons-material/Inbox";
import CallIcon from "@mui/icons-material/Call";
import TimelineIcon from "@mui/icons-material/Timeline";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ArticleIcon from "@mui/icons-material/Article";
import SendIcon from "@mui/icons-material/Send";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";

const AdminSidebar = ({ open, setOpen }) => {
  const { userData, isLoading, tataToken } = useSelector((state) => state.mainReducer);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const hitApi = useApi();
  const isLoggingOut = useRef(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showChangePwd, setShowChangePwd] = useState(false);
  const [pwdForm, setPwdForm] = useState({
    current: "",
    next: "",
    confirm: "",
  });
  const [showPwd, setShowPwd] = useState({
    current: false,
    next: false,
    confirm: false,
  });
  const [pwdLoading, setPwdLoading] = useState(false);
  const [pwdMsg, setPwdMsg] = useState(null); // { type: "success"|"error", text }

  const handlePwdChange = (field) => (e) =>
    setPwdForm((f) => ({ ...f, [field]: e.target.value }));

  const toggleShowPwd = (field) =>
    setShowPwd((s) => ({ ...s, [field]: !s[field] }));

  const handleCloseProfile = () => {
    setShowProfile(false);
    setShowChangePwd(false);
    setPwdForm({ current: "", next: "", confirm: "" });
    setPwdMsg(null);
  };

  const submitChangePwd = async () => {
    if (!pwdForm.current || !pwdForm.next || !pwdForm.confirm) {
      setPwdMsg({ type: "error", text: "All fields are required." });
      return;
    }
    if (pwdForm.next.length < 6) {
      setPwdMsg({
        type: "error",
        text: "New password must be at least 6 characters.",
      });
      return;
    }
    if (pwdForm.next !== pwdForm.confirm) {
      setPwdMsg({ type: "error", text: "New passwords do not match." });
      return;
    }
    setPwdLoading(true);
    setPwdMsg(null);
    try {
      const res = await fetch(`${apiPath}${apiChangeOwnPassword}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          currentPassword: pwdForm.current,
          newPassword: pwdForm.next,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setPwdMsg({ type: "success", text: "Password changed successfully!" });
        setPwdForm({ current: "", next: "", confirm: "" });
        setTimeout(() => {
          setShowChangePwd(false);
          setPwdMsg(null);
        }, 1800);
      } else {
        setPwdMsg({
          type: "error",
          text: data?.message || "Failed to change password.",
        });
      }
    } catch {
      setPwdMsg({ type: "error", text: "Network error. Please try again." });
    } finally {
      setPwdLoading(false);
    }
  };

  const {
    SUPERADMIN,
    ADMIN,
    RESOURCE_MANAGER,
    PROGRAM_MANAGER,
    DATABASE_MANAGER,
    AGENT,
    PRESALES_MANAGER,
    ALL,
  } = constants;

  const logOut = () => {
    isLoggingOut.current = true;
    hitApi(apiLogutUser, null, method.post, keyNames.userData);
  };

  useEffect(() => {
    if (isLoggingOut.current && isLoading === "noLoading") {
      isLoggingOut.current = false;
      // Tata SmartFlo logout
      if (tataToken) tataLogout(tataToken).catch(() => {});
      dispatch({
        type: keyNames.REUSABLE,
        keyName: keyNames.tataToken,
        payload: null,
      });
      sessionStorage.clear();
      dispatch({
        type: keyNames.REUSABLE,
        keyName: keyNames.userData,
        payload: null,
      });
      navigate("/");
    }
  }, [isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const allItems = [
    {
      key: "1",
      label: "Dashboard",
      link: "/app",
      icon: <SpeedIcon />,
      roles: ALL,
    },
    {
      key: "2",
      label: "Report",
      link: "/app/report",
      icon: <AssessmentIcon />,
      roles: [PROGRAM_MANAGER],
    },
    {
      key: "3",
      label: "Resource",
      link: "/app/employee-management",
      icon: <ManageAccountsIcon />,
      roles: [RESOURCE_MANAGER, ADMIN],
      activeChildrenPath: ["/app/add-employee", "/app/edit-employee"],
    },
    {
      key: "4",
      label: "Professional Fee",
      link: "/app/professional-fee",
      icon: <CurrencyRupeeIcon />,
      roles: [RESOURCE_MANAGER, ADMIN],
    },
    {
      key: "5",
      label: "Program",
      link: "/app/program-management",
      icon: <EventIcon />,
      roles: [PROGRAM_MANAGER],
      activeChildrenPath: [
        "/app/program-management/edit",
        "/app/program-management/program-detail",
      ],
    },
    {
      key: "6",
      label: "Database",
      link: "/app/database-management",
      icon: <StorageIcon />,
      roles: [DATABASE_MANAGER],
      activeChildrenPath: ["/app/add-data"],
    },
    {
      key: "7",
      label: "Company",
      link: "/app/company-management",
      icon: <CorporateFareIcon />,
      roles: [DATABASE_MANAGER],
      activeChildrenPath: [
        "/app/company-management/add-comapany",
        "/app/company-management/edit-comapany",
      ],
    },
    {
      key: "8",
      label: "Approval",
      link: "/app/approval-management",
      icon: <ApprovalIcon />,
      roles: [DATABASE_MANAGER],
    },
    {
      key: "9",
      label: "Linkedin",
      link: "/app/linkedin-profile-management",
      icon: <LinkedInIcon />,
      roles: [DATABASE_MANAGER],
      activeChildrenPath: ["/app/linkedin-profile-management/uploadData"],
    },
    {
      key: "20",
      label: "Individual Search",
      link: "/app/individual-search",
      icon: <ManageSearchIcon />,
      roles: [PRESALES_MANAGER],
    },
    {
      key: "19",
      label: "Engagements",
      link: "/app/contacts-engagement",
      icon: <TimelineIcon />,
      roles: [DATABASE_MANAGER],
      activeChildrenPath: ["/app/contacts-engagement/detail"],
    },
    {
      key: "10",
      label: "Agent",
      link: "/app/agent-management",
      icon: <SupportAgentIcon />,
      roles: [AGENT],
      activeChildrenPath: ["/app/agent-management/database"],
    },
    {
      key: "11",
      label: "Fees",
      link: "/app/agent-management/invoices",
      icon: <ReceiptIcon />,
      roles: [AGENT],
    },
    {
      key: "12",
      label: "Presales",
      link: "/app/presales-management",
      icon: <TrendingUpIcon />,
      roles: [PRESALES_MANAGER],
      activeChildrenPath: [
        "/app/presales-management/add-campaign",
        "/app/presales-management/edit-campaign",
        "/app/presales-management/view-campaign",
        "/app/presales-management/revision-data",
      ],
    },
    {
      key: "13",
      label: "Template",
      link: "/app/template-management",
      icon: <ArticleIcon />,
      roles: [PROGRAM_MANAGER],
      activeChildrenPath: [
        "/app/template-management/add-template",
        "/app/template-management/edit-template",
      ],
    },
    {
      key: "14",
      label: "Trigger",
      link: "/app/template-management/send-template",
      icon: <SendIcon />,
      roles: [PROGRAM_MANAGER],
      activeChildrenPath: ["/app/template-management/view-calling-data"],
    },
    {
      key: "16",
      label: "Professional Fees",
      link: "/app/invoice",
      icon: <CurrencyRupeeIcon />,
      roles: [PROGRAM_MANAGER],
    },
    {
      key: "17",
      label: "Call History",
      link: "/app/calling-history",
      icon: <CallIcon />,
      roles: [PROGRAM_MANAGER],
    },
    {
      key: "18",
      label: "Attendance",
      link: "/app/attendance",
      icon: <EventAvailableIcon />,
      roles: [PROGRAM_MANAGER, RESOURCE_MANAGER, ADMIN],
    },
  ];

  // Superadmin sees: Dashboard + RM items + DM items + Presales items
  const SUPERADMIN_KEYS = new Set([
    "1",
    "3",
    "4",
    "18",
    "6",
    "7",
    "8",
    "9",
    "19",
    "12",
    "13",
    "14",
  ]);

  const filteredItems =
    userData?.role === SUPERADMIN
      ? allItems.filter((item) => SUPERADMIN_KEYS.has(item.key))
      : allItems.filter((item) => item.roles.includes(userData?.role));

  return (
    <Box
      sx={{
        position: "fixed",
        left: 0,
        top: 0,
        height: "100vh",
        width: open ? 200 : 56,
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper",
        borderRight: "1px solid",
        borderColor: "divider",
        zIndex: 1200,
        transition: "width 0.2s ease",
        overflow: "visible",
        boxShadow: "2px 0 12px rgba(0,0,0,0.06)",
      }}
    >
      {/* Brand header + toggle */}
      <Box
        sx={{
          px: open ? 1.5 : 1,
          display: "flex",
          alignItems: "center",
          justifyContent: open ? "flex-start" : "center",
          borderBottom: "1px solid",
          borderColor: "divider",
          minHeight: 56,
          flexShrink: 0,
          position: "relative",
          overflow: "visible",
          gap: 1,
        }}
      >
        {/* Logo + title */}
        <img
          src="/KestoneArch.png"
          alt="Kestone"
          style={{
            height: 40,
            width: "auto",
            objectFit: "contain",
            flexShrink: 0,
          }}
        />
        {open && (
          <Typography
            variant="subtitle2"
            fontWeight={700}
            noWrap
            sx={{ color: "text.primary" }}
          >
            Kestone CRM
          </Typography>
        )}

        {/* Half-overflow expand / collapse arrow */}
        <Tooltip title={open ? "Collapse" : "Expand"} placement="right">
          <IconButton
            size="small"
            onClick={() => setOpen(!open)}
            sx={{
              position: "absolute",
              right: -14,
              top: "50%",
              transform: "translateY(-50%)",
              width: 28,
              height: 28,
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
              "&:hover": { bgcolor: "action.hover" },
              zIndex: 1201,
            }}
          >
            {open ? (
              <ChevronLeftIcon sx={{ fontSize: 16 }} />
            ) : (
              <ChevronRightIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Nav items */}
      <Box sx={{ flexGrow: 1, overflowY: "auto", overflowX: "hidden" }}>
        <List disablePadding sx={{ py: 0.5 }}>
          {filteredItems.map((item) => {
            const isActive =
              location.pathname === item.link ||
              item.activeChildrenPath?.some((p) =>
                location.pathname.startsWith(p)
              );

            const btn = (
              <ListItemButton
                key={item.key}
                onClick={() => navigate(item.link)}
                sx={{
                  minHeight: 44,
                  px: open ? 1.5 : 1,
                  justifyContent: open ? "flex-start" : "center",
                  borderRadius: 1.5,
                  mx: 0.5,
                  mb: 0.25,
                  bgcolor: isActive ? "primary.main" : "transparent",
                  color: isActive ? "#fff" : "text.secondary",
                  "&:hover": {
                    bgcolor: isActive ? "primary.dark" : "action.hover",
                    color: isActive ? "#fff" : "text.primary",
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 1.5 : 0,
                    justifyContent: "center",
                    color: "inherit",
                    "& svg": { fontSize: 20 },
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {open && (
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: 13,
                      fontWeight: isActive ? 600 : 400,
                      noWrap: true,
                    }}
                  />
                )}
              </ListItemButton>
            );

            return open ? (
              btn
            ) : (
              <Tooltip
                key={item.key}
                title={item.label}
                placement="right"
                arrow
              >
                {btn}
              </Tooltip>
            );
          })}
        </List>
      </Box>

      {/* Bottom: Profile + Logout */}
      <Box
        sx={{
          borderTop: "1px solid",
          borderColor: "divider",
          p: open ? 1 : 0.5,
          display: "flex",
          flexDirection: open ? "row" : "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.5,
        }}
      >
        <Tooltip title={userData?.employeeName || "Profile"} placement="right">
          <IconButton size="small" onClick={() => setShowProfile(true)}>
            <AccountCircle />
          </IconButton>
        </Tooltip>

        {open && (
          <Typography
            variant="caption"
            noWrap
            sx={{ flex: 1, ml: 0.5, fontWeight: 600 }}
          >
            {userData?.employeeName || "Profile"}
          </Typography>
        )}

        <Tooltip title="Logout" placement="right">
          <IconButton size="small" color="error" onClick={logOut}>
            <LogoutIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Profile dialog */}
      <Dialog
        PaperProps={{
          elevation: 8,
          sx: {
            position: "absolute",
            bottom: 64,
            left: open ? 212 : 68,
            m: 0,
            borderRadius: 3,
            width: 290,
            overflow: "hidden",
          },
        }}
        open={showProfile}
        onClose={handleCloseProfile}
      >
        {/* Header banner */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #0D9A8F 0%, #0A7A72 100%)",
            pt: 2.5,
            pb: 4,
            px: 2.5,
            position: "relative",
          }}
        >
          <IconButton
            size="small"
            onClick={handleCloseProfile}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "rgba(255,255,255,0.8)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.15)", color: "#fff" },
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>

          {/* Avatar */}
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              bgcolor: "rgba(255,255,255,0.2)",
              border: "2px solid rgba(255,255,255,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 1.5,
            }}
          >
            <Typography sx={{ fontSize: 22, fontWeight: 700, color: "#fff" }}>
              {(userData?.employeeName || "U").charAt(0).toUpperCase()}
            </Typography>
          </Box>

          <Typography
            variant="subtitle1"
            fontWeight={700}
            color="#fff"
            lineHeight={1.2}
          >
            {userData?.employeeName || "—"}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: "rgba(255,255,255,0.75)" }}
          >
            {userData?.email || ""}
          </Typography>

          {/* Role chip */}
          <Box sx={{ mt: 1 }}>
            <Chip
              label={(userData?.role || "")
                .replace(/_/g, " ")
                .replace(/\b\w/g, (c) => c.toUpperCase())}
              size="small"
              sx={{
                bgcolor: "rgba(255,255,255,0.18)",
                color: "#fff",
                fontWeight: 600,
                fontSize: 10,
                letterSpacing: 0.4,
                height: 20,
                border: "1px solid rgba(255,255,255,0.3)",
              }}
            />
          </Box>
        </Box>

        {/* Info rows */}
        <Box sx={{ px: 2, py: 1.5 }}>
          {[
            {
              icon: <BadgeIcon sx={{ fontSize: 15 }} />,
              label: "Emp. Code",
              value: userData?.employeeCode || "—",
            },
          ].map(({ icon, label, value }, i, arr) => (
            <Box key={label}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  py: 1,
                }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    bgcolor: "#0D9A8F14",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#0D9A8F",
                    flexShrink: 0,
                  }}
                >
                  {icon}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    lineHeight={1.2}
                    display="block"
                  >
                    {label}
                  </Typography>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {value}
                  </Typography>
                </Box>
              </Box>
              {i < arr.length - 1 && <Divider />}
            </Box>
          ))}
        </Box>

        {/* Change Password */}
        <Box sx={{ px: 2, pb: 1 }}>
          <Box
            onClick={() => {
              setShowChangePwd((v) => !v);
              setPwdMsg(null);
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 1,
              borderRadius: 2,
              cursor: "pointer",
              color: "#0D9A8F",
              border: "1px solid #0D9A8F30",
              bgcolor: "#0D9A8F08",
              transition: "background 0.2s",
              "&:hover": { bgcolor: "#0D9A8F14" },
            }}
          >
            <LockResetIcon sx={{ fontSize: 16 }} />
            <Typography
              variant="body2"
              fontWeight={600}
              fontSize={13}
              sx={{ flex: 1 }}
            >
              Change Password
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {showChangePwd ? "▲" : "▼"}
            </Typography>
          </Box>

          {showChangePwd && (
            <Box
              sx={{
                mt: 1.5,
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              {pwdMsg && (
                <Alert severity={pwdMsg.type} sx={{ py: 0, fontSize: 12 }}>
                  {pwdMsg.text}
                </Alert>
              )}
              {[
                { field: "current", label: "Current Password" },
                { field: "next", label: "New Password" },
                { field: "confirm", label: "Confirm New Password" },
              ].map(({ field, label }) => (
                <TextField
                  key={field}
                  size="small"
                  label={label}
                  type={showPwd[field] ? "text" : "password"}
                  value={pwdForm[field]}
                  onChange={handlePwdChange(field)}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        size="small"
                        onClick={() => toggleShowPwd(field)}
                        edge="end"
                      >
                        {showPwd[field] ? (
                          <VisibilityOff sx={{ fontSize: 16 }} />
                        ) : (
                          <Visibility sx={{ fontSize: 16 }} />
                        )}
                      </IconButton>
                    ),
                  }}
                  sx={{ "& .MuiInputBase-input": { fontSize: 13 } }}
                />
              ))}
              <Button
                variant="contained"
                size="small"
                disabled={pwdLoading}
                onClick={submitChangePwd}
                sx={{
                  bgcolor: "#0D9A8F",
                  "&:hover": { bgcolor: "#0A7A72" },
                  fontWeight: 600,
                }}
              >
                {pwdLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  "Update Password"
                )}
              </Button>
            </Box>
          )}
        </Box>

        {/* Footer logout */}
        <Box sx={{ px: 2, pb: 2, pt: 0.5 }}>
          <Box
            onClick={logOut}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 1,
              borderRadius: 2,
              cursor: "pointer",
              color: "#E82A2A",
              border: "1px solid #E82A2A30",
              bgcolor: "#E82A2A08",
              transition: "background 0.2s",
              "&:hover": { bgcolor: "#E82A2A14" },
            }}
          >
            <LogoutIcon sx={{ fontSize: 16 }} />
            <Typography variant="body2" fontWeight={600} fontSize={13}>
              Sign Out
            </Typography>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default AdminSidebar;
